import { type VisualizerNotice } from "../loader";

import {
  type Session,
  type SessionLoadFailure,
} from "./EphemeralWorkspaceState";

/** "session 'ss_1'", or "sessions 'ss_1', 'ss_2'" for several. */
function sessionPhrase(sessionIds: string[]): string {
  const noun = sessionIds.length === 1 ? "session" : "sessions";
  const quoted = sessionIds.map((sessionId) => `'${sessionId}'`).join(", ");
  return `${noun} ${quoted}`;
}

/** `phrase` with its first letter uppercased, to open a sentence. */
function capitalized(phrase: string): string {
  return `${phrase.charAt(0).toUpperCase()}${phrase.slice(1)}`;
}

/** The ids of the sessions that failed for `reason`, in the order given. */
function sessionIdsFailingFor(
  failures: SessionLoadFailure[],
  reason: SessionLoadFailure["reason"],
): string[] {
  return failures
    .filter((failure) => failure.reason === reason)
    .map((failure) => failure.sessionId);
}

/**
 * The banners the workspace raises for the session datasources it opened with:
 * one per reason a session could not be read, plus one for the sessions that
 * read back with no files. A session that loaded with files raises none.
 *
 * A session with no files is worth a banner because the workspace it opens is
 * blank, and a blank visualizer with nothing said about it reads as a bug.
 *
 * Sessions sharing an outcome go into one banner rather than one banner each.
 * A notice's `name` becomes the notification's id, so banners have to differ
 * by name: dismissing clears every notification under the dismissed id, and a
 * notification whose id is already shown is dropped. A collection sent to the
 * visualizer can also name more sessions than anyone would dismiss one at a
 * time.
 */
export function sessionNotices(
  sessions: Session[],
  sessionLoadFailures: SessionLoadFailure[],
): VisualizerNotice[] {
  const notices: VisualizerNotice[] = [];

  const notFoundIds = sessionIdsFailingFor(sessionLoadFailures, "not-found");
  if (notFoundIds.length > 0) {
    notices.push({
      name: "SessionNotFoundError",
      title: "Session not found",
      message: `${capitalized(sessionPhrase(notFoundIds))} not found.`,
      severity: "error",
    });
  }

  const unauthorizedIds = sessionIdsFailingFor(
    sessionLoadFailures,
    "unauthorized",
  );
  if (unauthorizedIds.length > 0) {
    notices.push({
      name: "SessionUnauthorizedError",
      title: "Unauthorized",
      // Access to a session comes from the data underneath it, so the remedy
      // names that rather than the session.
      message: `Viewing ${sessionPhrase(unauthorizedIds)} requires access to the underlying data.`,
      severity: "error",
    });
  }

  const unreadableIds = sessionIdsFailingFor(sessionLoadFailures, "unreadable");
  if (unreadableIds.length > 0) {
    notices.push({
      name: "SessionNotLoadedError",
      title: "Session not loaded",
      message: `${capitalized(sessionPhrase(unreadableIds))} could not be loaded.`,
      severity: "error",
    });
  }

  const emptySessionIds = sessions
    .filter((session) => session.files.length === 0)
    .map((session) => session.id);
  if (emptySessionIds.length > 0) {
    notices.push({
      name: "SessionEmptyNotice",
      title: "Nothing to visualize",
      message: `${capitalized(sessionPhrase(emptySessionIds))} ${emptySessionIds.length === 1 ? "has" : "have"} no files available to visualize.`,
      severity: "info",
    });
  }

  return notices;
}
